import {
  Badge,
  Box,
  Card,
  CardBody,
  CardHeader,
  Flex,
  HStack,
  Heading,
  IconButton,
  Progress,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import {
  differenceInCalendarDays,
  format,
  formatDistanceToNow,
  parseISO,
  subDays,
} from "date-fns";
import { FiRefreshCw } from "react-icons/fi";
import { FaMicrophone, FaCommentDots } from "react-icons/fa";
import { fetchController } from "../../../utils/FetchController/fetchController";
import { getHostDomain } from "../../../utils/utils";
import CustomDateRangePicker from "../../../utils/CustomDateRange/CustomDateRangePicker";
import ChannelDonut from "../../Dashboard/ChannelDonut";
import TopAssistantsChart from "../../Dashboard/TopAssistantsChart";
import useChannelBreakdown from "../../Dashboard/useChannelBreakdown";
import useTopAssistants from "../../Dashboard/useTopAssistants";
import useWallet from "../Billing/useWallet";
import { AgencyContext } from "../AgencyProvider/AgencyProvider";

ChartJS.register(...registerables);

const baseUrl = getHostDomain();

// The single metric driving the burn-trend chart and the wallet tile.
// This is the dollar amount deducted from the user's wallet per Usage
// event (see app/app/service/billing_service.py — both voice and text
// flows write this key when "billing_used" == "wallet").
const WALLET_METRIC_KEY = "utilized_wallet_balance";

const CURRENCY = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatKey(key) {
  return (key || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function getProgressColor(pct) {
  if (pct >= 100) return "red";
  if (pct >= 80) return "yellow";
  return "blue";
}

// One credit allocation rendered as a card.
function PlanHealthCard({
  label,
  used,
  total,
  formatValue = (v) => Number(v || 0).toLocaleString(),
  prev,
  iconColor,
}) {
  const pct = total > 0 ? (used / total) * 100 : 0;
  const colorScheme = getProgressColor(pct);
  const delta =
    prev && prev > 0 ? ((used - prev) / prev) * 100 : null;
  const deltaUp = delta != null && delta > 0;
  const deltaDown = delta != null && delta < 0;

  return (
    <Card variant="outline" h="100%">
      <CardBody>
        <Text
          fontSize="xs"
          textTransform="uppercase"
          color={iconColor || "gray.500"}
          fontWeight="bold"
          letterSpacing="0.5px"
          mb={2}
        >
          {label}
        </Text>
        <Flex align="baseline" gap={1} mb={1}>
          <Text fontSize="2xl" fontWeight="bold" color="gray.900">
            {formatValue(used)}
          </Text>
          {total > 0 && (
            <Text fontSize="sm" color="gray.500">
              / {formatValue(total)}
            </Text>
          )}
        </Flex>
        {total > 0 ? (
          <Progress
            value={Math.min(pct, 100)}
            colorScheme={colorScheme}
            size="sm"
            borderRadius="full"
            mb={2}
          />
        ) : (
          <Box h="8px" mb={2} />
        )}
        <Flex justify="space-between" align="center">
          <Text fontSize="xs" color="gray.500">
            {total > 0 ? `${pct.toFixed(0)}% used` : "Spent this cycle"}
          </Text>
          {delta != null && (
            <Tooltip label={`vs previous cycle (${formatValue(prev)})`}>
              <Badge
                colorScheme={deltaUp ? "red" : deltaDown ? "green" : "gray"}
                fontSize="10px"
              >
                {deltaUp ? "↑" : deltaDown ? "↓" : ""}
                {Math.abs(delta).toFixed(0)}%
              </Badge>
            </Tooltip>
          )}
        </Flex>
      </CardBody>
    </Card>
  );
}

// Daily wallet-spend line chart. No metric selector — wallet $ is the
// one trend that matters here; everything else (queries, voice secs)
// is already surfaced in the row 1 tiles.
function WalletBurnChart({ dailyItems }) {
  const { labels, values, total } = useMemo(() => {
    if (!dailyItems || dailyItems.length === 0)
      return { labels: [], values: [], total: 0 };
    const labels = [];
    const values = [];
    let total = 0;
    for (const it of dailyItems) {
      labels.push(format(parseISO(it.usage_date), "MMM d"));
      const v = Number(it?.aggregation?.[WALLET_METRIC_KEY] || 0);
      values.push(v);
      total += v;
    }
    return { labels, values, total };
  }, [dailyItems]);

  if (values.length === 0 || total === 0) {
    return (
      <Flex align="center" justify="center" h="240px">
        <Text fontSize="sm" color="gray.400">
          No wallet spend in this range
        </Text>
      </Flex>
    );
  }

  const config = {
    data: {
      labels,
      datasets: [
        {
          label: "Wallet spend",
          data: values,
          borderColor: "#059669",
          backgroundColor: "rgba(5, 150, 105, 0.1)",
          fill: true,
          tension: 0.3,
          borderWidth: 2,
          pointRadius: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: { boxWidth: 10, font: { size: 11 }, color: "#6B7280" },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${CURRENCY.format(ctx.parsed.y || 0)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: "#9CA3AF", font: { size: 10 }, maxRotation: 0 },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(156,163,175,0.15)" },
          ticks: {
            color: "#9CA3AF",
            font: { size: 10 },
            callback: (v) => CURRENCY.format(v),
          },
        },
      },
    },
  };

  return (
    <Box h="240px">
      <Line {...config} />
    </Box>
  );
}

const Usage = () => {
  const agency = useContext(AgencyContext);
  const { textColor } = agency;

  const [refreshKey, setRefreshKey] = useState(0);
  const [credits, setCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(true);

  const [dateRange, setDateRange] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
  });
  const [cycleEnd, setCycleEnd] = useState(null);

  const [dailyItems, setDailyItems] = useState([]);
  const [prevDailyItems, setPrevDailyItems] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(true);

  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);

  // Wallet from the existing hook — fetched once per mount; refreshKey
  // bumps a remount via a local key on the consumer below.
  const { wallet, status: walletStatus } = useWallet();

  // --- Fetchers ---

  const loadUserAndCycle = useCallback(async () => {
    try {
      const res = await fetchController(`${baseUrl}/api/v1/user`, "GET");
      const u = res.data;
      // Only update cycleEnd (used for the "Cycle resets in Nd" text).
      // Earlier this also reset dateRange to (next_credits_refresh - 30d, today),
      // which collapses to a 0-day window any time today is within 30 days
      // of the next refresh — i.e. for most users most of the time. The
      // charts would briefly render against the default last-30-days range
      // and then go empty when this override fired. Default range stays.
      if (u?.next_credits_refresh) {
        setCycleEnd(parseISO(u.next_credits_refresh));
      }
    } catch (e) {
      console.error("Failed to load user details", e);
    }
  }, []);

  const loadCredits = useCallback(async () => {
    setCreditsLoading(true);
    try {
      const data = await fetchController(
        `${baseUrl}/api/v1/analytics/user_credits`,
        "GET"
      );
      setCredits(data);
    } catch (e) {
      console.error("Failed to load user credits", e);
    } finally {
      setCreditsLoading(false);
    }
  }, []);

  const loadDaily = useCallback(async (range, target) => {
    if (!range?.startDate || !range?.endDate) return;
    const from = format(range.startDate, "yyyy-MM-dd");
    const to = format(range.endDate, "yyyy-MM-dd");
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/user_usage/user_daily_usages?size=100&date_from=${from}&date_to=${to}`,
        "GET"
      );
      target(res?.data?.items || []);
    } catch (e) {
      console.error("Failed to load daily usages", e);
      target([]);
    }
  }, []);

  const loadConversations = useCallback(async (range) => {
    if (!range?.startDate || !range?.endDate) return;
    setConversationsLoading(true);
    const from = format(range.startDate, "yyyy-MM-dd");
    const to = format(range.endDate, "yyyy-MM-dd");
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/conversation?date_from=${from}&date_to=${to}&size=20&page=1`,
        "GET"
      );
      setConversations(res?.data?.items || []);
    } catch (e) {
      console.error("Failed to load conversations", e);
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserAndCycle();
    loadCredits();
  }, [loadUserAndCycle, loadCredits, refreshKey]);

  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) return;
    setDailyLoading(true);
    const span = Math.max(
      1,
      differenceInCalendarDays(dateRange.endDate, dateRange.startDate)
    );
    const prevRange = {
      startDate: subDays(dateRange.startDate, span + 1),
      endDate: subDays(dateRange.startDate, 1),
    };
    Promise.all([
      loadDaily(dateRange, setDailyItems),
      loadDaily(prevRange, setPrevDailyItems),
    ]).finally(() => setDailyLoading(false));
    loadConversations(dateRange);
  }, [dateRange, loadDaily, loadConversations, refreshKey]);

  const { topAssistants } = useTopAssistants({ dateRange });
  const { channelBreakdown } = useChannelBreakdown({ dateRange });

  // --- Derived ---

  const sumKey = (items, key) =>
    items.reduce((s, it) => s + (it?.aggregation?.[key] || 0), 0);

  const walletSpentInRange = sumKey(dailyItems, WALLET_METRIC_KEY);
  const walletSpentPrev = sumKey(prevDailyItems, WALLET_METRIC_KEY);
  const walletBalance = typeof wallet?.wallet_balance === "number"
    ? wallet.wallet_balance
    : null;

  const daysUntilReset = cycleEnd
    ? Math.max(0, differenceInCalendarDays(cycleEnd, new Date()))
    : null;

  const planHealthMetrics = useMemo(() => {
    const out = [];
    const allocated = credits?.allocated || {};
    const utilized = credits?.utilized || {};
    for (const key of Object.keys(allocated)) {
      const a = allocated[key];
      if (typeof a !== "number" || a <= 0) continue;
      out.push({
        key,
        label: formatKey(key),
        used: utilized[key] || 0,
        total: a,
        prev: sumKey(prevDailyItems, key),
      });
    }
    return out;
  }, [credits, prevDailyItems]);

  return (
    <Flex direction="column" gap={4} w="100%" h="100%" overflowY="auto">
      {/* Header */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={3} px={2}>
        <Box>
          <Heading color={textColor} fontSize="xl">
            Your Usage
          </Heading>
          {cycleEnd && (
            <Text fontSize="sm" color="gray.500" mt={1}>
              Cycle resets in {daysUntilReset}d ·{" "}
              {format(cycleEnd, "MMM d, yyyy")}
            </Text>
          )}
        </Box>
        <HStack spacing={2}>
          <Box minW="260px">
            <CustomDateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(r) =>
                setDateRange({ startDate: r.startDate, endDate: r.endDate })
              }
            />
          </Box>
          <Tooltip label="Refresh">
            <IconButton
              icon={<FiRefreshCw />}
              size="sm"
              aria-label="Refresh"
              onClick={() => setRefreshKey((k) => k + 1)}
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* Row 1: Plan health + wallet tile */}
      <Box px={2}>
        {creditsLoading ? (
          <Flex justify="center" align="center" h="160px">
            <Spinner />
          </Flex>
        ) : (
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 5 }} spacing={3}>
            {planHealthMetrics.map((m) => (
              <PlanHealthCard
                key={m.key}
                label={m.label}
                used={m.used}
                total={m.total}
                prev={m.prev}
              />
            ))}
            <PlanHealthCard
              label="Wallet spent"
              used={walletSpentInRange}
              total={0}
              prev={walletSpentPrev}
              formatValue={(v) => CURRENCY.format(v || 0)}
              iconColor="green.600"
            />
          </SimpleGrid>
        )}
      </Box>

      {/* Row 2: Wallet-balance burn trend */}
      <Box px={2}>
        <Card variant="outline">
          <CardHeader pb={0}>
            <Flex justify="space-between" align="center">
              <Heading size="sm">Wallet spend trend</Heading>
              <HStack spacing={3}>
                {walletStatus !== "loading" && walletBalance != null && (
                  <Tag size="sm" colorScheme="blue" variant="subtle">
                    Balance: {CURRENCY.format(walletBalance)}
                  </Tag>
                )}
                <Tag size="sm" colorScheme="green" variant="subtle">
                  Spent in range: {CURRENCY.format(walletSpentInRange)}
                </Tag>
              </HStack>
            </Flex>
          </CardHeader>
          <CardBody>
            {dailyLoading ? (
              <Flex justify="center" align="center" h="240px">
                <Spinner />
              </Flex>
            ) : (
              <WalletBurnChart dailyItems={dailyItems} />
            )}
          </CardBody>
        </Card>
      </Box>

      {/* Row 3: Attribution */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3} px={2}>
        <Card variant="outline">
          <CardHeader pb={0}>
            <Heading size="sm">Top assistants</Heading>
            <Text fontSize="xs" color="gray.500">
              by conversation count
            </Text>
          </CardHeader>
          <CardBody>
            <TopAssistantsChart topAssistants={topAssistants} />
          </CardBody>
        </Card>
        <Card variant="outline">
          <CardHeader pb={0}>
            <Heading size="sm">By channel</Heading>
            <Text fontSize="xs" color="gray.500">
              by conversation count
            </Text>
          </CardHeader>
          <CardBody>
            <ChannelDonut channelBreakdown={channelBreakdown} />
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Row 4: Recent activity log */}
      <Box px={2} pb={4}>
        <Card variant="outline">
          <CardHeader pb={0}>
            <Heading size="sm">Recent calls & chats</Heading>
            <Text fontSize="xs" color="gray.500">
              Latest conversations in this date range
            </Text>
          </CardHeader>
          <CardBody>
            {conversationsLoading ? (
              <Flex justify="center" align="center" h="120px">
                <Spinner />
              </Flex>
            ) : conversations.length === 0 ? (
              <Flex align="center" justify="center" h="120px">
                <Text fontSize="sm" color="gray.400">
                  No conversations in this range
                </Text>
              </Flex>
            ) : (
              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>When</Th>
                      <Th>Type</Th>
                      <Th>Assistant</Th>
                      <Th>Contact</Th>
                      <Th>Channel</Th>
                      <Th isNumeric>Messages</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {conversations.map((c) => {
                      const when = c.created_at ? parseISO(c.created_at) : null;
                      const contactName = [c.contact_first_name, c.contact_last_name]
                        .filter(Boolean)
                        .join(" ") || "Anonymous";
                      return (
                        <Tr key={c.id}>
                          <Td>
                            <Tooltip
                              label={when ? format(when, "PPpp") : ""}
                            >
                              <Text fontSize="xs">
                                {when ? formatDistanceToNow(when, { addSuffix: true }) : "—"}
                              </Text>
                            </Tooltip>
                          </Td>
                          <Td>
                            {c.includes_voice ? (
                              <HStack spacing={1} color="purple.600">
                                <FaMicrophone size={11} />
                                <Text fontSize="xs">Voice</Text>
                              </HStack>
                            ) : (
                              <HStack spacing={1} color="blue.600">
                                <FaCommentDots size={11} />
                                <Text fontSize="xs">Chat</Text>
                              </HStack>
                            )}
                          </Td>
                          <Td>
                            <Text fontSize="xs" noOfLines={1} maxW="180px">
                              {c.assistant_name || "—"}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="xs" noOfLines={1} maxW="160px">
                              {contactName}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="xs" color="gray.600">
                              {c.widget_type || "—"}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text fontSize="xs">{c.chat_count ?? 0}</Text>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </CardBody>
        </Card>
      </Box>
    </Flex>
  );
};

export default Usage;
